import { useEffect, useRef } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import MobileBottomNav from '@/components/layout/MobileBottomNav'
import ConflictResolutionModal from '@/components/modals/ConflictResolutionModal'
import FailedMutationsSheet from '@/components/modals/FailedMutationsSheet'
import { Navbar } from '@/components/navbar'
import InstallBanner from '@/components/pwa/InstallBanner'
import OfflineBanner from '@/components/pwa/OfflineBanner'
import { useServiceWorkerNavigate } from '@/hooks/pwa/usePendingSync'
import { useScrollRestoration } from '@/hooks/ui/useScrollRestoration'
import { useSWAutoUpdate } from '@/hooks/useSWAutoUpdate'
import { HeartIcon } from '@/icons'

export default function Layout() {
  useSWAutoUpdate()
  useScrollRestoration()
  useServiceWorkerNavigate()
  const mainRef = useRef<HTMLElement>(null)
  const location = useLocation()
  const isFirstRender = useRef(true)

  // SPA navigation should move focus to <main> so screen-reader and keyboard
  // users aren't stranded at the top-of-document or at the previous focus.
  // Skip the very first render so focus doesn't steal from the initial page.
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false
      return
    }
    mainRef.current?.focus()
  }, [location.pathname])

  return (
    <div className="min-h-screen bg-base flex flex-col">
      <a href="#main" className="skip-link">
        Skip to main content
      </a>
      <Navbar />
      <OfflineBanner />
      <InstallBanner />

      <main
        id="main"
        ref={mainRef}
        tabIndex={-1}
        className="flex-1 px-4 sm:px-16 lg:px-32 py-8 pb-24 md:pb-8 w-full focus:outline-none"
      >
        <Outlet />
      </main>

      <footer className="hidden md:block bg-elevated border-t border-theme-border py-4">
        <div className="px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-muted text-sm">
          <span>TreasureLog - One Piece TCG Collection Tracker</span>
          <span className="hidden sm:inline text-dim">|</span>
          <a
            href="https://ko-fi.com/treasurelog"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 hover:text-pink-300 transition-colors"
            aria-label="Support TreasureLog on Ko-fi"
          >
            <HeartIcon className="w-4 h-4" />
            <span>Support</span>
          </a>
        </div>
      </footer>

      <MobileBottomNav />
      <FailedMutationsSheet />
      <ConflictResolutionModal />
    </div>
  )
}
